import { useLocation } from "react-router-dom";
import { ExternalLink, Calendar, FileText } from "lucide-react";
import SEO from "../components/SEO";
import { CALENDLY_URL } from "../lib/site";

const JOTFORM_BASE = "https://form.jotform.com/261115325389053";

const SYSTEM_LABELS = {
  attendance: "Attendance Intervention System",
  behavior: "Behavior Consistency System",
  enrollment: "Enrollment Follow Through System",
  district: "District Rollout Plan",
};

export default function BookDemo() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const system = params.get("system");
  const systemLabel = system ? SYSTEM_LABELS[system] : null;

  // Forward every inbound query param to Jotform so the form captures full
  // context (system, schools, approach, gap, etc.) from the originating page.
  const jotformHref = search ? `${JOTFORM_BASE}${search}` : JOTFORM_BASE;

  return (
    <>
      <SEO
        title="Request a Demo | Aligned Education Systems"
        description="Request a demo with Aligned Education Systems to see how our weekly action systems help schools improve attendance, behavior response, and enrollment."
      />

      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="book-demo-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-14 lg:pt-24 lg:pb-20">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]" data-testid="book-demo-eyebrow">
            {systemLabel ? `Get Started · ${systemLabel}` : "Get Started"}
          </div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] leading-[1.05] max-w-4xl">
            Request a Demo
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-2xl">
            {systemLabel
              ? `Tell us a little about your school or district and we'll send over pricing and implementation specifics for the ${systemLabel}.`
              : "Tell us a little about your school or district and we'll schedule a focused walkthrough aligned to your priorities."}
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#F4F1EB]" data-testid="book-demo-content">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* REQUEST INFORMATION (Jotform — opens in new tab) */}
          <div className="lg:col-span-7 bg-[#FDFBF7] border border-[#0A192F]/10 p-8 lg:p-10 flex flex-col">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-[#52796F]" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-widest text-[#52796F]">Option 01</div>
            </div>
            <h2 className="mt-5 font-serif-display text-2xl sm:text-3xl text-[#0A192F] leading-snug">
              Request Pricing &amp; Implementation Overview
            </h2>
            <p className="mt-4 text-[#4A5568] leading-relaxed">
              Share a few details about your school or district and we&apos;ll send back the full pricing and implementation overview.
            </p>
            <ul className="mt-6 space-y-2 text-[#1A1D20] text-sm">
              <li className="flex items-start gap-2">
                <span className="w-4 h-px bg-[#D4AF37] mt-2.5 flex-shrink-0" />
                <span>Takes about a minute</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-px bg-[#D4AF37] mt-2.5 flex-shrink-0" />
                <span>Tailored to your role &amp; priorities</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-px bg-[#D4AF37] mt-2.5 flex-shrink-0" />
                <span>No obligation</span>
              </li>
            </ul>
            <a
              href={jotformHref}
              target="_blank"
              rel="noreferrer"
              className="mt-auto pt-8 inline-flex items-center justify-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F] self-start"
              data-testid="jotform-open-btn"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> Open the Request Form
            </a>
            <p className="mt-3 text-xs text-[#4A5568]">Opens in a new tab.</p>
          </div>

          {/* CALENDLY */}
          <div className="lg:col-span-5">
            <div className="bg-[#FDFBF7] border border-[#0A192F]/10 p-6 h-full flex flex-col">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-[#52796F]" strokeWidth={1.5} />
                <div className="text-xs uppercase tracking-widest text-[#52796F]">Option 02</div>
              </div>
              <h2 className="mt-5 font-serif-display text-2xl text-[#0A192F]">Schedule Directly</h2>
              <p className="mt-2 text-[#4A5568]">Pick a time that works for you.</p>
              <div className="mt-4 border border-[#0A192F]/10 flex-1 min-h-[480px]" data-testid="calendly-embed-wrapper">
                <iframe
                  src={CALENDLY_URL}
                  title="Schedule with Aligned Education Systems"
                  className="w-full h-full"
                  style={{ border: "0", minHeight: "480px" }}
                  data-testid="calendly-iframe"
                />
              </div>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-block text-sm text-[#0A192F] border-b border-[#0A192F] pb-0.5 self-start"
                data-testid="calendly-external-link"
              >
                Open calendar in a new tab →
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
